<template>
  <div>
    <v-tabs v-model="tab" class="mb-4">
      <v-tab>Пользователи</v-tab>
      <v-tab>Роли пользователей</v-tab>
      <v-tab>Active Directory</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <UsersTable
          :users="users"
          :loading="loading"
          @assign-permission="handleAssignPermission"
        />
      </v-tab-item>
      <v-tab-item>
        <RolesTab :permissions="allPermissions" />
      </v-tab-item>
      <v-tab-item>
        <ActiveDirectoryTab @sync-complete="handleSyncComplete" />
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="showRoleDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Выберите роль</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedPermission"
            :items="permissionItems"
            item-text="name"
            item-value="code"
            label="Роль"
            return-object
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showRoleDialog = false">Отмена</v-btn>
          <v-btn color="primary" @click="handleRoleSelected">Выбрать</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showConfirmDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Подтверждение</v-card-title>
        <v-card-text>
          <p>
            Вы уверены, что хотите назначить роль
            <strong>{{ selectedPermissionName }}</strong>
            выбранным пользователям ({{ selectedUsersCount }})?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmDialog = false">Отмена</v-btn>
          <v-btn color="primary" :loading="assigning" @click="confirmAssign">Подтвердить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import UsersTable from './UsersTable.vue'
import RolesTab from './RolesTab.vue'
import ActiveDirectoryTab from './ActiveDirectoryTab.vue'
import authApi from '@/services/authApi'

export default {
  name: 'AdminMainView',
  components: {
    UsersTable,
    RolesTab,
    ActiveDirectoryTab
  },
  data() {
    return {
      tab: 0,
      loading: false,
      assigning: false,
      showRoleDialog: false,
      showConfirmDialog: false,
      selectedPermission: null,
      selectedUserIds: []
    }
  },
  computed: {
    ...mapGetters('auth', ['getUsers', 'getUsersFetching', 'currentUser']),
    users() {
      return this.getUsers || []
    },
    allPermissions() {
      return [
        { code: 'app.aggregator', name: 'Доступ к агрегатору' },
        { code: 'app.aggregator.admin', name: 'Может управлять поставщиками' },
        { code: 'app.catalog', name: 'Доступ к каталогу' },
        { code: 'app.catalog.admin', name: 'Может редактировать элементы' },
        { code: 'app.images', name: 'Доступ к изображениям' },
        { code: 'app.images.admin', name: 'Может модерировать изображения' },
        { code: 'api.1c.directions', name: 'Просмотр товарных направлений' },
        { code: 'api.1c.distributors', name: 'Просмотр списка поставщиков' },
        { code: 'api.auth.get_users', name: 'Получение полного списка пользователей агрегатора' },
        { code: 'api.parser.brands', name: 'Чтение брендов поставщиков' },
        { code: 'api.parser.brands.update', name: 'Модификация брендов поставщиков' },
        { code: 'api.parser.categories', name: 'Просмотр категорий поставщиков' },
        { code: 'api.parser.categories.update', name: 'Модификация категорий поставщиков' },
        { code: 'api.parser.distributors', name: 'Просмотр поставщиков' },
        { code: 'api.parser.distributors.update', name: 'Модификация поставщиков' },
        { code: 'api.parser.features', name: 'Просмотр характеристик товаров поставщиков' },
        { code: 'api.parser.features.update', name: 'Модификация характеристик товаров' },
        { code: 'api.parser.modules', name: 'Просмотр модулей парсинга для поставщиков' },
        { code: 'api.parser.products', name: 'Просмотр товаров поставщиков' },
        { code: 'api.parser.products.update', name: 'Модификация товаров поставщиков' },
        { code: 'api.parser.units', name: 'Просмотр единиц измерения поставщиков' },
        { code: 'api.parser.units.update', name: 'Модификация единиц измерения поставщиков' },
        { code: 'api.stock.brands-categories', name: 'Просмотр категорий брендов' },
        { code: 'api.stock.brands', name: 'Просмотр брендов' },
        { code: 'api.stock.brands.update', name: 'Модификация брендов' },
        { code: 'api.stock.categories', name: 'Просмотр ветвей ассортимента' },
        { code: 'api.stock.categories.update', name: 'Модификация ветвей ассортимента' },
        { code: 'api.stock.features', name: 'Просмотр характеристик товаров' },
        { code: 'api.stock.features.update', name: 'Модификация характеристик товаров' },
        { code: 'api.stock.products', name: 'Просмотр товаров' },
        { code: 'api.stock.products.update', name: 'Модификация товаров' },
        { code: 'api.stock.structure', name: 'Просмотр папок номенклатуры' },
        { code: 'api.stock.structure.update', name: 'Модификация папок номенклатуры' },
        { code: 'api.stock.units', name: 'Просмотр единиц измерения' },
        { code: 'api.stock.units.update', name: 'Модификация единиц измерения' },
        { code: 'api.users.reset_password', name: 'Право на сброс пароля других пользователей' },
        { code: 'api.images', name: 'Просмотр статистики, и пр. сведений касательно модерации и статистики по ней' },
        { code: 'api.images.admin', name: 'полный доступ к модерации' },
        { code: 'api.images.moderate', name: 'Право на модерацию изображений (принятие/отклонение)' },
        { code: 'api.images.upload', name: 'Загрузка новых изображений на модерацию ко всем сущностям' },
        { code: 'api.images.delete_existing', name: 'Удаление изображений непосредственно из товара/категории/ветви ассортимента' },
        { code: 'api.moderation.payment', name: 'Право платить за загруженные фото' }
      ]
    },
    permissionItems() {
      return this.allPermissions.map(p => ({
        code: p.code,
        name: `${p.code} - ${p.name}`
      }))
    },
    selectedPermissionName() {
      if (!this.selectedPermission) return ''
      const perm = this.allPermissions.find(p => p.code === this.selectedPermission.code)
      return perm ? `${perm.code} - ${perm.name}` : this.selectedPermission.code
    },
    selectedUsersCount() {
      return this.selectedUserIds.length
    }
  },
  created() {
    if (!this.currentUser || !this.currentUser.is_superuser) {
      this.$router.push({ name: 'dashboard' })
      return
    }
    this.loadUsers()
  },
  methods: {
    ...mapActions('auth', ['actionFetchUsers']),
    async loadUsers() {
      this.loading = true
      try {
        await this.actionFetchUsers()
      } catch (error) {
        this.$store.dispatch('common/actionAddErrorMessage', error.message || 'Ошибка загрузки пользователей')
      } finally {
        this.loading = false
      }
    },
    handleAssignPermission(userIds) {
      this.selectedUserIds = userIds
      this.showRoleDialog = true
    },
    handleRoleSelected() {
      if (!this.selectedPermission) {
        this.$store.dispatch('common/actionAddErrorMessage', 'Выберите роль')
        return
      }
      this.showRoleDialog = false
      this.showConfirmDialog = true
    },
    async confirmAssign() {
      this.assigning = true
      try {
        await authApi.assignPermission(
          this.selectedUserIds,
          this.selectedPermission.code
        )
        this.$store.dispatch('common/actionAddInfoMessage', 'Роль успешно назначена')
        this.showConfirmDialog = false
        this.selectedPermission = null
        this.selectedUserIds = []
        await this.loadUsers()
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || 'Ошибка назначения роли'
        this.$store.dispatch('common/actionAddErrorMessage', errorMsg)
      } finally {
        this.assigning = false
      }
    },
    async handleSyncComplete() {
      // Обновляем список пользователей после синхронизации с AD
      await this.loadUsers()
    }
  }
}
</script>
